import {
  RichTextMarkdownArtifactCanvas,
  useMarkdownArtifactDraft,
  MARKDOWN_FIELD_MAX_LENGTH,
  markdownFieldLengthError,
} from '@yeshuman/sdk'
import {
  managedOpportunityStructuredFields,
  useOpportunity,
  useOpportunityMutations,
  type OrganisationOpportunity,
} from '../../hooks/use-organisation-opportunities'
import { BiasCoachBanner } from '../bias/bias-coach-banner'
import { detect, joinedText } from '../../lib/bias-guardrail'

interface JobDescriptionCanvasProps {
  opportunityId: string
  opportunity: OrganisationOpportunity
  onClose: () => void
  onSaved?: (updated: OrganisationOpportunity) => void
}

/** Description-only artifact canvas — header title is the sole visible label (no duplicate field label). */
export function JobDescriptionCanvas({
  opportunityId,
  opportunity,
  onClose,
  onSaved,
}: JobDescriptionCanvasProps) {
  const { updateOpportunity, isUpdating, updateError } = useOpportunityMutations(opportunityId)
  const live = useOpportunity(opportunityId)
  const current = live.data ?? opportunity
  const draft = useMarkdownArtifactDraft(current.description ?? '', opportunityId)

  const handleSave = () => {
    if (draft.charCount > MARKDOWN_FIELD_MAX_LENGTH) {
      draft.setValidationError(
        `Job description must be at most ${MARKDOWN_FIELD_MAX_LENGTH} characters.`,
      )
      return
    }

    if (!draft.value.trim()) {
      draft.setValidationError('Job description is required')
      return
    }

    const descriptionLengthErr = markdownFieldLengthError(draft.value, 'Job description')
    if (descriptionLengthErr) {
      draft.setValidationError(descriptionLengthErr)
      return
    }

    draft.setValidationError(null)

    void updateOpportunity({
      title: current.title,
      description: draft.value.trim(),
      ...managedOpportunityStructuredFields(current),
    })
      .then((updated) => {
        onSaved?.(updated)
        onClose()
      })
      .catch(() => {
        // Server error surfaces via updateError
      })
  }

  const biasFindings = detect(joinedText(current.title, draft.value), 'job_description')

  return (
    <>
      <BiasCoachBanner findings={biasFindings} source="edit" />
      <RichTextMarkdownArtifactCanvas
        id="artifact-job-description"
        editorChrome="document"
        value={draft.value}
        onChange={draft.onChange}
        charCount={draft.charCount}
        onCharCountChange={draft.onCharCountChange}
        validationError={draft.validationError}
        serverError={updateError?.message}
        onClose={onClose}
        onSave={handleSave}
        isDirty={draft.isDirty}
        isSaving={isUpdating}
      />
    </>
  )
}
